import React, { useEffect, useMemo } from 'react';
import { useT } from '../i18n/LanguageContext';
import { useSocialFeed } from '../hooks/use-social-feed';

function InstagramEmbed({ url }) {
  return (
    <blockquote
      className="instagram-media"
      data-instgrm-permalink={url}
      data-instgrm-version="14"
      style={{ background: '#FFF', border: 0, borderRadius: 12, margin: 0, width: '100%', minWidth: 0 }}
    />
  );
}

function TikTokEmbed({ url }) {
  const videoId = url.match(/\/video\/(\d+)/)?.[1] || '';
  return (
    <blockquote
      className="tiktok-embed"
      cite={url}
      data-video-id={videoId}
      style={{ maxWidth: '100%', minWidth: 260, margin: '0 auto' }}
    >
      <section>
        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      </section>
    </blockquote>
  );
}

export default function SocialFeed() {
  const t = useT();
  const { data } = useSocialFeed();
  const items = useMemo(() => data?.items || [], [data]);

  // Both platforms only scan the DOM for their blockquote markup when their
  // embed.js runs, so a fresh <script> load (re-triggering the scan) is
  // needed every time the curated item list changes.
  useEffect(() => {
    if (!items.length) return undefined;
    const scripts = [];
    if (items.some((i) => i.platform === 'instagram')) {
      const s = document.createElement('script');
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true;
      s.onload = () => window.instgrm?.Embeds?.process();
      document.body.appendChild(s);
      scripts.push(s);
    }
    if (items.some((i) => i.platform === 'tiktok')) {
      const s = document.createElement('script');
      s.src = 'https://www.tiktok.com/embed.js';
      s.async = true;
      document.body.appendChild(s);
      scripts.push(s);
    }
    return () => scripts.forEach((s) => s.remove());
  }, [items]);

  if (!items.length) return null;

  return (
    <section className="py-20 md:py-28 bg-[#f7f5f0]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#c8a25a] text-sm font-semibold tracking-widest uppercase mb-2">{t('socialFeed.eyebrow')}</p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#14213d]">{t('socialFeed.title')}</h2>
          <p className="mt-3 text-gray-500 text-lg max-w-xl mx-auto">{t('socialFeed.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {items.slice(0, 9).map((item) => (
            <div key={item.id} className="rounded-2xl bg-white p-2 shadow-[0_10px_30px_-15px_rgba(11,33,61,0.15)] flex justify-center">
              {item.platform === 'instagram' ? <InstagramEmbed url={item.url} /> : <TikTokEmbed url={item.url} />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
